/**
 * <InstallAppPrompt /> — mobile-only "Download Vigilant" banner.
 *
 * Two paths because mobile browsers diverge:
 *
 *   • Chrome / Edge / Samsung on Android fire `beforeinstallprompt` —
 *     we catch it, gate it behind a custom button, and call .prompt()
 *     when the user taps. Native install dialog appears.
 *
 *   • iOS Safari does NOT fire `beforeinstallprompt`. The only way to
 *     install is the Share → "Add to Home Screen" sheet. We detect
 *     iOS Safari and render a tasteful instructional banner that
 *     points the user at the Share button.
 *
 * Dismissal is persisted in localStorage with a 14-day cooldown
 * (key: `ve.pwa.dismissedAt`) so we don't pester users who said no.
 * Once installed (display-mode: standalone), the banner stops
 * rendering forever for that device.
 */
import React, { useEffect, useState } from 'react';
import { Download, X, Share, MoreVertical } from 'lucide-react';

const DISMISS_KEY = 've.pwa.dismissedAt';
const DISMISS_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

function isMobile() {
  if (typeof window === 'undefined') return false;
  // The viewport breakpoint is the most reliable signal — UA sniffing
  // is too brittle on modern Android tablets / iPadOS-as-Mac etc.
  return window.matchMedia?.('(max-width: 820px)').matches;
}

function isStandalone() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)').matches
    || window.navigator?.standalone === true; // iOS Safari
}

function isIOSSafari() {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent || '';
  const isIOS = /iphone|ipad|ipod/i.test(ua) || (
    // iPad-as-Mac fallback
    /Mac/.test(ua) && typeof document !== 'undefined' && 'ontouchend' in document
  );
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return isIOS && isSafari;
}

function dismissedRecently() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!Number.isFinite(ts)) return false;
    return (Date.now() - ts) < DISMISS_COOLDOWN_MS;
  } catch { return false; }
}

export default function InstallAppPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState('chrome'); // 'chrome' | 'ios'

  useEffect(() => {
    // Hard gates first — only show on mobile, only when NOT already
    // installed, and only if the user hasn't dismissed recently.
    if (!isMobile() || isStandalone() || dismissedRecently()) return undefined;

    // Path A — Chrome/Edge/Samsung on Android.
    const onBIP = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setMode('chrome');
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBIP);

    // Once installed, hide the banner forever for this device.
    const onInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    };
    window.addEventListener('appinstalled', onInstalled);

    // Path B — iOS Safari. No event to listen for, so we just show
    // the instructional banner after a short delay (gives the page
    // some time to render before nagging).
    let iosTimer;
    if (isIOSSafari()) {
      iosTimer = setTimeout(() => {
        setMode('ios');
        setVisible(true);
      }, 3500);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBIP);
      window.removeEventListener('appinstalled', onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === 'accepted') {
        // The appinstalled handler will hide the banner; clean up anyway.
        setVisible(false);
      }
    } catch { /* user cancelled */ }
    setDeferredPrompt(null);
  };

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-3 left-3 right-3 z-[80] max-w-md mx-auto"
      data-testid="install-app-prompt"
      role="dialog"
      aria-label="Install Vigilant as an app"
    >
      <div className="rounded-xl border border-[#8B3FC5]/50 bg-gradient-to-br from-[#1A0B22]/95 to-[#0A0410]/95 backdrop-blur-xl shadow-2xl shadow-[#8B3FC5]/20 p-4 flex items-start gap-3">
        <div className="h-10 w-10 rounded-lg bg-[#8B3FC5]/20 flex items-center justify-center shrink-0 border border-[#8B3FC5]/40">
          <Download size={18} className="text-[#8B3FC5]"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#8B3FC5] font-bold">
            Download as app
          </div>
          <div className="text-sm font-semibold text-white mt-0.5">
            Install Vigilant on your home screen
          </div>
          {mode === 'chrome' ? (
            <p className="text-[11px] text-slate-300 mt-1.5 leading-relaxed">
              Full-screen, push-ready, opens in one tap. No App Store needed.
            </p>
          ) : (
            <p className="text-[11px] text-slate-300 mt-1.5 leading-relaxed">
              Tap <Share size={11} className="inline -mt-0.5 mx-0.5 text-[#8B3FC5]"/>
              <span className="font-semibold text-white">Share</span> below, then choose
              {' '}<span className="font-semibold text-white">&ldquo;Add to Home Screen&rdquo;</span>.
            </p>
          )}
          <div className="flex items-center gap-2 mt-3">
            {mode === 'chrome' ? (
              <>
                <button
                  onClick={install}
                  data-testid="install-app-install-btn"
                  className="flex-1 px-3 py-2 text-xs font-semibold bg-[#8B3FC5] text-white rounded-lg hover:bg-[#9F4FDC] transition-colors flex items-center justify-center gap-1.5"
                >
                  <Download size={13}/>
                  Install
                </button>
                <button
                  onClick={dismiss}
                  data-testid="install-app-dismiss-btn"
                  className="px-3 py-2 text-xs text-slate-300 hover:text-white border border-white/10 rounded-lg"
                >
                  Not now
                </button>
              </>
            ) : (
              <button
                onClick={dismiss}
                data-testid="install-app-dismiss-btn"
                className="ml-auto px-3 py-2 text-xs text-slate-300 hover:text-white border border-white/10 rounded-lg"
              >
                Got it
              </button>
            )}
          </div>
        </div>
        <button
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          className="text-slate-500 hover:text-white absolute top-2.5 right-2.5"
          data-testid="install-app-close-btn"
        >
          <X size={14}/>
        </button>
      </div>
    </div>
  );
}
